import React, { useState, useEffect, useRef } from "react";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { useNavigate, Navigate } from "react-router-dom";
import { Lock, EnvelopeSimple, ShieldCheck, Clock, ArrowLeft, HourglassMedium, Eye, EyeSlash, Key } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function Login() {
  const { user, login, verifyEmail, resendCode } = useAuth();
  const nav = useNavigate();

  // 4 sahne: "credentials" → "code" → "waiting_admin" · ayrıca "forgot" modal
  const [step, setStep] = useState("credentials");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [code, setCode] = useState("");
  const [msg, setMsg] = useState("");
  const [deviceName, setDeviceName] = useState("");
  const [isFirstDevice, setIsFirstDevice] = useState(true);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);

  // Şifremi unuttum modalı
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSending, setForgotSending] = useState(false);
  const [forgotDone, setForgotDone] = useState(false);
  const [forgotErr, setForgotErr] = useState("");

  const codeInputRef = useRef(null);

  useEffect(() => {
    if (step === "code" && codeInputRef.current) codeInputRef.current.focus();
  }, [step]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  if (user) return <Navigate to="/" replace />;

  const doLogin = async (e) => {
    e.preventDefault();
    setLoading(true); setErr("");
    try {
      const res = await login(email, password);
      if (res.status === "ok") {
        toast.success("Hoş geldiniz");
        nav("/");
      } else if (res.status === "verification_required") {
        setDeviceName(res.device_name || "");
        setIsFirstDevice(!!res.is_first_device);
        setMsg(res.message || "Doğrulama kodu e-postanıza gönderildi.");
        setStep("code");
        setResendCooldown(30);
        toast.info("Doğrulama kodu e-postanıza gönderildi");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail) setErr(formatApiError(detail));
      else if (err?.message?.includes("Network")) setErr("Sunucuya bağlanılamadı. İnternet bağlantınızı kontrol edin.");
      else { setErr(err?.message || "Bir hata oluştu. Tekrar deneyin."); console.error("Login error:", err); }
    } finally { setLoading(false); }
  };

  const doVerify = async (e) => {
    e.preventDefault();
    if (code.trim().length !== 6) return setErr("Kod 6 haneli olmalı");
    setLoading(true); setErr("");
    try {
      const res = await verifyEmail(email, code.trim());
      if (res.status === "ok") {
        toast.success("Doğrulandı. Hoş geldiniz!");
        nav("/");
      } else if (res.status === "pending_admin_approval") {
        setMsg(res.message);
        setStep("waiting_admin");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setErr(detail || err?.message || "Kod doğrulanamadı");
    } finally { setLoading(false); }
  };

  const doResend = async () => {
    if (resendCooldown > 0) return;
    setLoading(true); setErr("");
    try {
      await resendCode(email, password);
      toast.success("Yeni kod gönderildi");
      setResendCooldown(30);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setErr(detail || "Kod tekrar gönderilemedi");
    } finally { setLoading(false); }
  };

  const backToCredentials = () => {
    setStep("credentials");
    setCode(""); setErr(""); setMsg("");
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    const target = (forgotEmail || "").trim().toLowerCase();
    if (!target || !target.includes("@")) {
      setForgotErr("Geçerli bir e-posta adresi girin");
      return;
    }
    setForgotSending(true); setForgotErr("");
    try {
      await api.post("/auth/forgot-password", { email: target });
      setForgotDone(true);
      toast.success("Talebiniz sistem yöneticisine iletildi");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setForgotErr(detail || err?.message || "Talep gönderilemedi. Tekrar deneyin.");
    } finally {
      setForgotSending(false);
    }
  };

  const closeForgot = () => {
    setForgotOpen(false);
    setForgotDone(false);
    setForgotErr("");
    setForgotSending(false);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Sol taraf hero */}
      <div className="hidden lg:flex flex-col justify-between p-12 relative overflow-hidden"
           style={{ background: "#0D110E", color: "#F2F0EB" }}>
        <div className="absolute inset-0 opacity-40" style={{
          backgroundImage: "url('https://images.unsplash.com/photo-1780106185042-505caa34c31a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBkYWlyeSUyMGZhcm0lMjBvdXRkb29yc3xlbnwwfHx8fDE3ODQxNDkyNTl8MA&ixlib=rb-4.1.0&q=85')",
          backgroundSize: "cover", backgroundPosition: "center", filter: "grayscale(30%)"
        }} />
        <div className="relative z-10">
          <img src="/seher-logo.png" alt="Seher Tarım ve Hayvancılık"
               className="max-h-20 w-auto object-contain bg-white/95 rounded-lg px-4 py-2" />
        </div>
        <div className="relative z-10">
          <h1 className="heading text-5xl leading-[1.05] mb-4">Süt üretiminizi<br/>rakamlarla yönetin.</h1>
          <p className="text-[#c7ccc4] max-w-md">Sürü, veteriner, ilaç stoğu, süt üretimi ve finansal veriler tek panelde. Yapay zeka destekli haftalık raporlarla kararlarınızı verilere dayandırın.</p>
        </div>
        <div className="relative z-10 text-sm text-[#8a938c]">© 2026 Seher Tarım ve Hayvancılık · Türkiye</div>
      </div>

      {/* Sağ taraf form */}
      <div className="flex items-center justify-center p-8 grid-bg">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center justify-center mb-8">
            <img src="/seher-logo.png" alt="Seher" className="max-h-14 w-auto object-contain" />
          </div>

          {step === "credentials" && (
            <>
              <h2 className="heading text-3xl mb-2">Giriş yap</h2>
              <p className="text-sm text-[#5e675e] mb-8">Çiftlik yönetim panelinize erişin.</p>
              <form onSubmit={doLogin} className="space-y-5" data-testid="login-form">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-[#6b746c]">E-posta</label>
                  <div className="mt-2 flex items-center border border-[#E5E2DC] rounded-md px-3 bg-white">
                    <EnvelopeSimple size={18} className="text-[#8a938c]" />
                    <input data-testid="login-email" type="email" required value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="flex-1 px-3 py-3 outline-none text-sm bg-transparent"
                      placeholder="ornek@ciftlik.com" />
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-semibold uppercase tracking-wider text-[#6b746c]">Şifre</label>
                    <button
                      type="button"
                      data-testid="forgot-password-link"
                      onClick={() => { setForgotOpen(true); setForgotEmail(email || ""); setForgotDone(false); setForgotErr(""); }}
                      className="text-xs text-[#417251] hover:underline"
                    >
                      Şifremi unuttum
                    </button>
                  </div>
                  <div className="mt-2 flex items-center border border-[#E5E2DC] rounded-md px-3 bg-white">
                    <Lock size={18} className="text-[#8a938c]" />
                    <input data-testid="login-password" type={showPassword ? "text" : "password"} required value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="flex-1 px-3 py-3 outline-none text-sm bg-transparent" placeholder="••••••••" />
                    <button
                      type="button"
                      data-testid="toggle-password-visibility"
                      onClick={() => setShowPassword((s) => !s)}
                      className="p-1 text-[#8a938c] hover:text-[#2D4A36] transition-colors"
                      aria-label={showPassword ? "Şifreyi gizle" : "Şifreyi göster"}
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeSlash size={18} weight="duotone" /> : <Eye size={18} weight="duotone" />}
                    </button>
                  </div>
                </div>
                {err && <div data-testid="login-error" className="text-sm text-[#C45B45] bg-[#fbeee9] border border-[#f2d4cb] px-3 py-2 rounded-md">{err}</div>}
                <button data-testid="login-submit" disabled={loading} type="submit"
                  className="w-full py-3.5 rounded-md bg-[#1A1D1A] text-white font-semibold hover:bg-[#2D4A36] disabled:opacity-60">
                  {loading ? "Kontrol ediliyor..." : "Panele giriş yap"}
                </button>
              </form>
              <div className="mt-8 text-xs text-[#6b746c] leading-relaxed">
                <strong className="text-[#1A1D1A]">Güvenlik:</strong> Yeni bir cihazdan girişte e-postanıza 6 haneli kod gelir. Bilinmeyen cihazlar için admin onayı gerekir.
              </div>
            </>
          )}

          {step === "code" && (
            <>
              <button onClick={backToCredentials} className="flex items-center gap-1 text-sm text-[#5e675e] hover:text-[#1A1D1A] mb-4">
                <ArrowLeft size={16} /> Geri
              </button>
              <div className="flex items-center gap-2 mb-2">
                <ShieldCheck size={26} weight="duotone" className="text-[#417251]" />
                <h2 className="heading text-3xl">Doğrulama kodu</h2>
              </div>
              <p className="text-sm text-[#5e675e] mb-2">
                <b>{email}</b> adresine 6 haneli bir kod gönderdik.
              </p>
              {deviceName && <p className="text-xs text-[#8a938c] mb-6">Cihaz: <b>{deviceName}</b> · Süre: 10 dakika</p>}
              {!isFirstDevice && (
                <div className="text-xs bg-[#F7EBCC] border border-[#DDA74F] text-[#7a5a10] px-3 py-2 rounded-md mb-4" data-testid="new-device-notice">
                  🔒 Yeni cihaz tespit edildi — kodu girdikten sonra admin onayı beklenecek.
                </div>
              )}
              <form onSubmit={doVerify} className="space-y-4" data-testid="verify-form">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-[#6b746c]">6 Haneli Kod</label>
                  <input
                    ref={codeInputRef}
                    data-testid="verify-code"
                    type="text" inputMode="numeric" pattern="[0-9]{6}" maxLength={6}
                    required value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    className="mt-2 w-full text-center px-3 py-4 text-3xl font-black tracking-[12px] tabnum border-2 border-[#E5E2DC] rounded-md bg-white focus:border-[#2D4A36] outline-none"
                    placeholder="000000"
                  />
                </div>
                {err && <div data-testid="verify-error" className="text-sm text-[#C45B45] bg-[#fbeee9] border border-[#f2d4cb] px-3 py-2 rounded-md">{err}</div>}
                <button data-testid="verify-submit" disabled={loading || code.length !== 6} type="submit"
                  className="w-full py-3.5 rounded-md bg-[#1A1D1A] text-white font-semibold hover:bg-[#2D4A36] disabled:opacity-60">
                  {loading ? "Doğrulanıyor..." : "Doğrula ve giriş yap"}
                </button>
                <button type="button" data-testid="verify-resend" onClick={doResend} disabled={resendCooldown > 0 || loading}
                  className="w-full text-sm text-[#417251] hover:underline disabled:text-[#8a938c] disabled:cursor-not-allowed">
                  {resendCooldown > 0 ? `Yeniden gönder (${resendCooldown}s)` : "Kodu tekrar gönder"}
                </button>
              </form>
            </>
          )}

          {step === "waiting_admin" && (
            <div className="text-center py-8" data-testid="waiting-admin">
              <div className="inline-block p-4 bg-[#F7EBCC] rounded-full mb-4">
                <HourglassMedium size={48} weight="duotone" className="text-[#8B6914]" />
              </div>
              <h2 className="heading text-2xl mb-3">Admin Onayı Bekleniyor</h2>
              <p className="text-[#5e675e] mb-6 leading-relaxed">
                {msg || "E-postanız doğrulandı. Cihazınız admin onayına gönderildi."}
              </p>
              <div className="text-xs text-[#8a938c] mb-6 p-3 bg-[#FAF8F4] rounded-md border border-[#EEECE5]">
                <b>Ne yapmam gerek?</b><br/>
                Sistem yöneticiniz size onay verdiğinde tekrar giriş yapabileceksiniz. Onay sonrası bu cihazdan artık sadece şifreyle girebileceksiniz.
              </div>
              <button onClick={backToCredentials} data-testid="waiting-back-btn"
                className="text-sm text-[#417251] hover:underline">
                ← Farklı hesapla giriş yap
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Şifremi Unuttum Modal */}
      {forgotOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: "rgba(13, 17, 14, 0.55)" }}
          data-testid="forgot-modal"
          onClick={closeForgot}
        >
          <div
            className="bg-white rounded-lg shadow-2xl w-full max-w-md p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3 mb-4">
              <div className="p-2 bg-[#F7EBCC] rounded-full">
                <Key size={22} weight="duotone" className="text-[#8B6914]" />
              </div>
              <div>
                <h3 className="heading text-xl">Şifremi Unuttum</h3>
                <p className="text-xs text-[#6b746c]">Talebiniz sistem yöneticisine iletilir.</p>
              </div>
            </div>

            {!forgotDone ? (
              <form onSubmit={submitForgot} className="space-y-4" data-testid="forgot-form">
                <div className="text-sm text-[#5e675e] bg-[#FAF8F4] border border-[#EEECE5] rounded-md p-3">
                  Şifreniz <b>otomatik sıfırlanmaz</b>. E-postanızı girin — sistem yöneticinize bildirim gönderilecek ve sizinle iletişime geçip yeni şifrenizi belirleyecek.
                </div>

                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-[#6b746c]">E-posta</label>
                  <div className="mt-2 flex items-center border border-[#E5E2DC] rounded-md px-3 bg-white">
                    <EnvelopeSimple size={18} className="text-[#8a938c]" />
                    <input
                      data-testid="forgot-email"
                      type="email"
                      required
                      autoFocus
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      className="flex-1 px-3 py-3 outline-none text-sm bg-transparent"
                      placeholder="ornek@ciftlik.com"
                    />
                  </div>
                </div>

                {forgotErr && (
                  <div data-testid="forgot-error" className="text-sm text-[#C45B45] bg-[#fbeee9] border border-[#f2d4cb] px-3 py-2 rounded-md">
                    {forgotErr}
                  </div>
                )}

                <div className="flex gap-2 pt-2">
                  <button
                    type="button"
                    data-testid="forgot-cancel"
                    onClick={closeForgot}
                    className="flex-1 py-2.5 rounded-md border border-[#E5E2DC] text-sm font-semibold text-[#5e675e] hover:bg-[#FAF8F4]"
                  >
                    Vazgeç
                  </button>
                  <button
                    type="submit"
                    data-testid="forgot-submit"
                    disabled={forgotSending}
                    className="flex-1 py-2.5 rounded-md bg-[#1A1D1A] text-white text-sm font-semibold hover:bg-[#2D4A36] disabled:opacity-60"
                  >
                    {forgotSending ? "Gönderiliyor..." : "Yöneticiye ilet"}
                  </button>
                </div>
              </form>
            ) : (
              <div className="py-2 space-y-4" data-testid="forgot-done">
                <div className="p-4 bg-[#E4EEE6] border border-[#c2d6c8] rounded-md text-sm text-[#2D4A36]">
                  ✓ Talebiniz alındı. Sistem yöneticiniz kısa süre içinde sizinle iletişime geçecek ve yeni şifrenizi güvenli şekilde iletecek.
                </div>
                <button
                  onClick={closeForgot}
                  data-testid="forgot-close"
                  className="w-full py-2.5 rounded-md bg-[#1A1D1A] text-white text-sm font-semibold hover:bg-[#2D4A36]"
                >
                  Kapat
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
